import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getReadinessBadgeColor, getReadinessColor } from '../../../utils/readinessHelpers';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  X,
  ArrowRight,
  ChartLineUp,
  Calendar,
  SoccerBall,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { baseUrl } from '../../../hooks/useEndpoints';
import {
  ICoachSessionAthlete,
  coachSessionsEndpoints,
} from '../../../endpoints/coachSessions';
import { useEndpoints } from '../../../hooks/useEndpoints';
import styles from './AthleteDrawer.module.scss';

/**
 * Drawer laterale slide-in per dettaglio atleta nel Roster.
 * Spec v1.1.1 sez 6 righe 447-454:
 *  - Storico RI ultimi 14 giorni (grafico)
 *  - Ultime 5 sessioni (presente/differenziato/infortunato)
 *  - Minuti giocati ultima gara
 *  - Note sul giocatore
 *  - Link "Vedi profilo completo →"
 */
export const AthleteDrawer: FC<{
  athlete: ICoachSessionAthlete;
  onClose: () => void;
}> = ({ athlete, onClose }) => {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const endpoints = useEndpoints();

  const { data: history = [] } = useQuery({
    queryKey: ['readinessHistory', athlete.athleteId],
    queryFn: () =>
      axios
        .get<{
          history: Array<{ date: string; interpolation: number }>;
          benchmark: unknown;
        }>(`${baseUrl}/readiness/${athlete.athleteId}/history`, {
          params: { limit: 14 },
        })
        .then((r) => (Array.isArray(r.data) ? r.data : (r.data?.history ?? []))),
  });

  // Ultime 5 sessioni del team dove l'atleta era coinvolto
  const { data: teamSessions = [] } = useQuery({
    queryKey: ['athleteLastSessions', athlete.athleteId, teamId],
    queryFn: () =>
      coachSessionsEndpoints
        .list({ teamId: teamId ?? '', status: 'executed' })
        .then((r) => r.data.slice(0, 20)),
    enabled: !!teamId,
  });

  // Filtra solo sessioni in cui l'atleta appare
  const athleteSessions = teamSessions.slice(0, 5);

  // Match sessions recenti del team
  const { data: matches = [] } = useQuery({
    queryKey: ['teamMatches', teamId, athlete.athleteId],
    queryFn: () =>
      endpoints
        .listMatchSessions({ teamId: teamId ?? '' })
        .then((r: any) => r.data ?? [])
        .catch(() => []),
    enabled: !!teamId,
  });

  const lastMatch = matches[0];
  const lastMatchMinutes = lastMatch?.minutesPlayed?.find(
    (m: any) => m.athleteId === athlete.athleteId,
  );

  const points = history.slice(-14);
  const max = 100;
  const min = 0;

  const openProfile = () => {
    navigate(`/dashboard/users/${athlete.athleteId}`);
  };

  // Soglie RI dal canone @leopart/calc (70/50/30) via readinessHelpers.
  const riColor = (ri: number) => getReadinessBadgeColor(getReadinessColor(ri));

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.name}>
              {athlete.lastName} {athlete.firstName}
            </div>
            {athlete.role && (
              <div className={styles.role}>
                {athlete.role}
                {athlete.shirtNumber != null && ` · #${athlete.shirtNumber}`}
              </div>
            )}
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.body}>
          {/* RI current */}
          <div className={styles.riCurrent}>
            <div
              className={styles.riBadge}
              style={{
                background: riColor(athlete.recoveryIndex ?? 0),
              }}
            >
              {athlete.recoveryIndex ?? '—'}
            </div>
            <div>
              <div className={styles.riLabel}>Recovery Index oggi</div>
              <div className={styles.riStatus}>
                Status:{' '}
                <strong>
                  {athlete.status === 'available'
                    ? 'Disponibile'
                    : athlete.status === 'differentiated'
                      ? 'Differenziato'
                      : 'Infortunato'}
                </strong>
              </div>
            </div>
          </div>

          {/* Storico 14gg */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <ChartLineUp size={13} /> Storico RI · ultimi 14 giorni
            </div>
            {points.length > 0 ? (
              <div className={styles.chart}>
                <svg viewBox="0 0 280 80" className={styles.chartSvg}>
                  {/* Gridlines */}
                  <line
                    x1="0"
                    y1="20"
                    x2="280"
                    y2="20"
                    stroke="#eee"
                    strokeDasharray="2,2"
                  />
                  <line
                    x1="0"
                    y1="40"
                    x2="280"
                    y2="40"
                    stroke="#eee"
                    strokeDasharray="2,2"
                  />
                  <line
                    x1="0"
                    y1="60"
                    x2="280"
                    y2="60"
                    stroke="#eee"
                    strokeDasharray="2,2"
                  />
                  {/* Polyline */}
                  <polyline
                    fill="none"
                    stroke="#063444"
                    strokeWidth="2"
                    points={points
                      .map((p, i) => {
                        const x = (i / Math.max(points.length - 1, 1)) * 280;
                        const y =
                          80 -
                          ((p.interpolation - min) / (max - min)) * 80;
                        return `${x.toFixed(1)},${y.toFixed(1)}`;
                      })
                      .join(' ')}
                  />
                  {/* Dots */}
                  {points.map((p, i) => {
                    const x = (i / Math.max(points.length - 1, 1)) * 280;
                    const y =
                      80 - ((p.interpolation - min) / (max - min)) * 80;
                    return (
                      <circle
                        key={i}
                        cx={x}
                        cy={y}
                        r="3"
                        fill={riColor(p.interpolation)}
                      />
                    );
                  })}
                </svg>
                <div className={styles.chartLabels}>
                  <span>
                    {dayjs(points[0]?.date).format('D MMM')}
                  </span>
                  <span>
                    {dayjs(points[points.length - 1]?.date).format('D MMM')}
                  </span>
                </div>
              </div>
            ) : (
              <div className={styles.empty}>Nessun dato RI disponibile</div>
            )}
          </div>

          {/* Ultime sessioni */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <Calendar size={13} /> Ultime 5 sessioni team
            </div>
            {athleteSessions.length > 0 ? (
              <div className={styles.sessionList}>
                {athleteSessions.map((s) => (
                  <div key={s._id} className={styles.sessionRow}>
                    <div className={styles.sessionDate}>
                      {dayjs(s.scheduledDate).format('D MMM')}
                    </div>
                    <div className={styles.sessionTitle}>{s.title}</div>
                    {s.metrics?.avgIPS != null && (
                      <div className={styles.sessionIps}>
                        IPS {s.metrics.avgIPS.toFixed(1)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.empty}>
                Nessuna sessione eseguita recente
              </div>
            )}
          </div>

          {/* Ultima gara */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <SoccerBall size={13} /> Ultima partita
            </div>
            {lastMatch && lastMatchMinutes ? (
              <div className={styles.matchBox}>
                <div className={styles.matchHeader}>
                  vs {lastMatch.opponentName ?? 'Avversario'}{' '}
                  <span className={styles.matchDate}>
                    {dayjs(lastMatch.scheduledDate).format('D MMM')}
                  </span>
                </div>
                <div className={styles.matchStats}>
                  <div>
                    <span className={styles.matchStatLabel}>Minuti</span>
                    <span className={styles.matchStatValue}>
                      {lastMatchMinutes.minutes ?? 0}'
                    </span>
                  </div>
                  {lastMatchMinutes.percentage != null && (
                    <div>
                      <span className={styles.matchStatLabel}>% partita</span>
                      <span className={styles.matchStatValue}>
                        {lastMatchMinutes.percentage}%
                      </span>
                    </div>
                  )}
                  {lastMatchMinutes.performanceRating != null && (
                    <div>
                      <span className={styles.matchStatLabel}>Prestazione</span>
                      <span className={styles.matchStatValue}>
                        {lastMatchMinutes.performanceRating}/10
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : lastMatch ? (
              <div className={styles.matchEmpty}>
                Panchina / non convocato nell'ultima partita
              </div>
            ) : (
              <div className={styles.matchEmpty}>
                Nessuna partita registrata per il team
              </div>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.profileBtn}
            onClick={openProfile}
          >
            Vedi profilo completo <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};
